// Scoping a canvas to ONE experiment, and reopening it against another.
//
// **Two different facts, kept apart on purpose.** A dashboard's `experimentKey`
// (the row's `scope`) is what it is FILED under — it is why the board appears on
// that experiment's page. The experiment PARAMETER, and the control it binds, is
// what actually narrows the numbers. A board can be filed under one experiment
// and read against another (that is what "reopen against another experiment"
// means), so the two must not be collapsed into one field, and a page that
// changes one has to decide about the other rather than assume.
//
// **Rebinding is a document transform, never a copy.** Pointing a board at a
// second experiment rewrites one control's default and one parameter's default,
// which is a pure function from `StudioDoc` to `StudioDoc` — so it undoes, it
// saves, and it produces exactly the document a reader would have got by
// picking that experiment in the control by hand.
import type {
  ExperimentSourceRef,
  ExperimentSummary,
  FilterState,
  NativeFilterSpec,
} from "@bi/shared/types";

import { defaultFilterState } from "./filter-state";
import { EXPERIMENT_PARAMETER } from "./studio-starter";
import { type StudioDoc, putControl, putParameter } from "./studio-model";
import { applyParameters } from "./studio-template";

/** Which sources carry rows for `key`, and the column naming it in each.
 *
 *  An empty map is deliberately ambiguous at this level and must be
 *  disambiguated by the caller: it means either "this key is not in the
 *  register" or "the register has not been computed yet", and the register
 *  response's own `status` is the only thing that separates them. */
export function coverageFor(
  experiments: readonly ExperimentSummary[],
  key: string,
): ExperimentSourceRef[] {
  const row = experiments.find((e) => e.key === key);
  return row ? [...row.sources] : [];
}

export function coveredSourceIds(coverage: readonly ExperimentSourceRef[]): string[] {
  return [...new Set(coverage.map((c) => c.sourceId))].sort();
}

/** The experiment control on a document: the one a parameter named `experiment`
 *  points at, falling back to the first parameter's control.
 *
 *  The fallback exists because a board built before this convention (or one
 *  whose author named the parameter something else) still has exactly one
 *  parameter, and refusing to rebind it would mean the affordance quietly does
 *  nothing on the boards most likely to need it. */
export function experimentControlOf(doc: StudioDoc): NativeFilterSpec | null {
  const parameters = doc.spec.parameters ?? [];
  const parameter =
    parameters.find((p) => p.name === EXPERIMENT_PARAMETER) ?? parameters[0] ?? null;
  if (!parameter) return null;
  return doc.spec.filters.find((f) => f.id === parameter.filter_id) ?? null;
}

/** Whether this document can be pointed at another experiment at all. A board
 *  with no parameter is a dashboard, not a template, and the UI says so rather
 *  than offering a picker that would change nothing. */
export function isExperimentTemplate(doc: StudioDoc): boolean {
  return experimentControlOf(doc) !== null;
}

/** The experiment this document currently reads, from its parameter's default.
 *  `null` when it is not a template, or when the parameter carries no default. */
export function boundExperiment(doc: StudioDoc): string | null {
  const parameters = doc.spec.parameters ?? [];
  const parameter =
    parameters.find((p) => p.name === EXPERIMENT_PARAMETER) ?? parameters[0] ?? null;
  return parameter?.default ?? null;
}

/**
 * Point this document at `key`.
 *
 * Both halves move together: the parameter's default (what a save stores, and
 * what an unfiltered reopen falls back to) and the control's `default_value`
 * (what the filter bar shows the moment the board paints). Moving only one is
 * the bug that makes a board show one experiment's name over another's numbers.
 *
 * Returns the document unchanged when there is nothing to rebind, so a caller
 * can offer the action unconditionally and let this decide.
 */
export function rebindExperiment(doc: StudioDoc, key: string): StudioDoc {
  const control = experimentControlOf(doc);
  if (!control) return doc;
  const parameters = doc.spec.parameters ?? [];
  const parameter =
    parameters.find((p) => p.name === EXPERIMENT_PARAMETER) ?? parameters[0] ?? null;
  if (!parameter) return doc;
  const rebound = putControl(doc, { ...control, default_value: [key] });
  return putParameter(rebound, { ...parameter, default: key });
}

/**
 * The live `FilterState` a document opens on for `key`.
 *
 * Routed through `applyParameters` rather than assembled here, because that
 * function already owns the one thing worth getting right: a parameter that
 * cannot be bound is REPORTED rather than silently ignored, and a board showing
 * every experiment under one experiment's name is the failure this whole file
 * exists to avoid.
 */
export function experimentFilterState(
  doc: StudioDoc,
  key: string | null,
  base: FilterState = defaultFilterState(),
): { filters: FilterState; unbound: string[] } {
  const { filters, unbound } = applyParameters(
    doc.spec.parameters ?? [],
    doc.spec.filters,
    key ? { [EXPERIMENT_PARAMETER]: key } : {},
    base,
  );
  return { filters, unbound };
}
